import { useMemo } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import get from 'lodash/get'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import DnsIcon from '@mui/icons-material/Dns'
import { useTeam } from '../../../contexts/TeamContext'
import { useProjects } from '../../../contexts/ProjectsContext'
import { usePackageImports } from '../../../contexts/PackageImportsContext'
import { useNodes } from '../../../contexts/NodesContext'
import { useServices } from '../../../contexts/ServicesContext'
import useNodeSelection from '../../../shared/hooks/useNodeSelection'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import NodeChipFilter from '../../../shared/components/NodeChipFilter'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import Page from '../../../shared/components/layouts/Page'
import ResourceList from '../../../shared/components/layouts/ResourceList'
import ResourceListRow from '../../../shared/components/layouts/ResourceListRow'
import ResourceListCell from '../../../shared/components/layouts/ResourceListCell'
import Flex from '../../../shared/components/layouts/Flex'
import FlexColumn from '../../../shared/components/layouts/FlexColumn'
import ProjectStatusIndicator from '../../../shared/components/ProjectStatusIndicator'
import ProjectDomainsChips from '../../../shared/components/ProjectDomainsChips'
import PackageImportStatusChip from '../../packages/imports/components/PackageImportStatusChip'

function PackageImportIndexPage() {
  const { teamSlug } = useParams()
  const { team } = useTeam()
  const { projectsById, loading: projectsLoading } = useProjects()
  const { packageImports, loading: packageImportsLoading, error } = usePackageImports()
  const { nodes, nodesById, loading: nodesLoading } = useNodes()
  const { servicesByProjectId, loading: servicesLoading } = useServices()

  const { activeNodeId, handleSelectNode } = useNodeSelection('packageImports.selectedNodeId', nodes)

  const loading = projectsLoading || packageImportsLoading || nodesLoading || servicesLoading

  const filteredPackageImports = useMemo(() => {
    if (!activeNodeId) return packageImports
    return packageImports.filter((pi) => {
      const project = get(projectsById, pi.projectId)
      return project?.nodeIds?.includes(activeNodeId)
    })
  }, [packageImports, projectsById, activeNodeId])

  const pendingCountByNodeId = useMemo(() => {
    const counts = {}
    packageImports.forEach((pi) => {
      if (pi.pendingChanges) {
        const project = get(projectsById, pi.projectId)
        project?.nodeIds?.forEach((nodeId) => {
          counts[nodeId] = (counts[nodeId] || 0) + 1
        })
      }
    })
    return counts
  }, [packageImports, projectsById])

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="packageImports.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="packageImports.teamIndex.title" />}
        entities={{ team }}
        actions={
          <Button
            component={RouterLink}
            to={`/teams/${teamSlug}/packages/marketplace`}
            variant="contained"
            color="primary"
          >
            <FormattedMessage id="packageImports.teamIndex.importFromMarketplace" />
          </Button>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, overflow: 'hidden', minHeight: 0, width: '100%' }}>
        <Box sx={{ flexShrink: 0, minWidth: { md: 200 }, borderRight: { xs: 0, md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: 'divider', overflowY: { xs: 'hidden', md: 'auto' }, py: { xs: 1, md: 3 }, px: 2 }}>
          <NodeChipFilter
            nodes={nodes}
            activeNodeId={activeNodeId}
            onSelect={handleSelectNode}
            pendingCountByNodeId={pendingCountByNodeId}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', py: 3, px: 2 }}>
          <ResourceList
            items={filteredPackageImports}
            renderItem={(packageImport) => {
              const project = get(projectsById, packageImport.projectId)
              const services = get(servicesByProjectId, project?.id, [])

              return (
                <ResourceListRow
                  key={packageImport.id}
                  to={`/teams/${teamSlug}/docker/package-imports/${packageImport.id}`}
                >
                  <ResourceListCell width={40}>
                    <ProjectStatusIndicator services={services} />
                  </ResourceListCell>

                  <ResourceListCell grow>
                    <FlexColumn gap={0.5}>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {packageImport.package?.name || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {project?.name || '-'}
                      </Typography>
                      <Flex gap={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                        <ProjectDomainsChips projectId={project?.id} />
                        {project?.nodeIds?.map((nodeId) => get(nodesById, nodeId))
                          .filter(Boolean)
                          .map((node) => (
                            <Chip
                              key={node.id}
                              icon={<DnsIcon />}
                              label={node.name}
                              size="small"
                              color="secondary"
                              clickable
                              component={RouterLink}
                              to={`/teams/${teamSlug}/nodes/${node.id}`}
                            />
                          ))}
                      </Flex>
                    </FlexColumn>
                  </ResourceListCell>

                  <ResourceListCell shrink={false}>
                    <PackageImportStatusChip packageImport={packageImport} />
                  </ResourceListCell>
                </ResourceListRow>
              )
            }}
            emptyMessageId="packageImports.teamIndex.empty"
          />
        </Box>
      </Box>
    </Page>
  )
}

export default PackageImportIndexPage
